/**

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
import { useApi } from '@backstage/core-plugin-api';
import { useCallback, useMemo } from 'react';
import useObservable from 'react-use/lib/useObservable';
import { DocRef, starredDocsApiRef } from '../apis/StarredDocsApi';

/** @public */
export function useStarredDocs(): {
  starredDocs: DocRef[];
  toggleStarredDoc: (docRef: DocRef) => void;
  isStarredDoc: (docRef: string) => boolean;
} {
  const starredDocsApi = useApi(starredDocsApiRef);

  const starredDocs = useObservable(
    useMemo(() => starredDocsApi.starredDoc$(), [starredDocsApi]),
    [],
  );

  const isStarredDoc = useCallback(
    (docKey: string) =>
      !!starredDocs.find(starredDoc => docKey === starredDoc.key),
    [starredDocs],
  );

  const toggleStarredDoc = useCallback(
    (docRef: DocRef): void => {
      // fire-and-forget while preserving error handling without returning a Promise
      void (async () => {
        try {
          await starredDocsApi.toggleStarred(docRef);
        } catch {
          // swallow or log if desired
        }
      })();
    },
    [starredDocsApi],
  );

  return {
    starredDocs,
    toggleStarredDoc,
    isStarredDoc,
  };
}
